import {
  SandboxApplicant,
  HttpApplicantClient as HttpApplicantClientFromSdk,
  AsyncContainer,
  HttpContentService,
  Sandbox,
  GottyClient,
  HttpClientRequestError,
  SandboxEvent,
  UnavailableTokenError,
  TTYEvent
} from '@sandbox/api-sdk';
import { AxiosRequestConfig } from 'axios';
import { getSandbox } from '@/server/model/timeline';
import router from '@/router';

function createPromise<T = any>() {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason: any) => void;
  const promise = new Promise<T>((rs, rj) => {
    resolve = rs;
    reject = rj;
  });
  return { resolve, reject, promise };
}

interface GetOption {
  cached?: boolean | undefined;
}

class HttpApplicantClient extends HttpApplicantClientFromSdk {
  public constructor(option?: AxiosRequestConfig) {
    super('', option as any);
  }
  public requestSandboxPool({
    poolName,
    token
  }: {
    storageId?: string;
    token: string;
    poolName: string;
  }) {
    return getSandbox({ pool_name: poolName, token });
  }
}

export function applySandboxByPoolName(data: {
  storageId?: string;
  token: string;
  poolName: string;
}) {
  const client = new HttpApplicantClient();
  const applicant = new SandboxApplicant(client);
  return applicant.getSandboxFromPool(data);
}

interface SandboxConfig {
  token: string;
  poolName: string;
  isProd?: boolean;
}
export const SandboxConfigKey = 'SandboxConfig';

export class SandboxContainer extends AsyncContainer {
  public constructor() {
    super();
    this.bind(HttpContentService, this.bindContentService);
    this.bind(Sandbox, this.bindSandbox);
  }
  public clearSandboxConfig() {
    this.clear(SandboxConfigKey);
  }
  public clearContentSerivce() {
    this.clear(HttpContentService);
  }
  public clearSandbox() {
    this.clear(Sandbox);
  }
  public clearGotty() {
    this.clear(GottyClient);
  }
  public getContentService(opt?: GetOption) {
    return this.get<HttpContentService>(HttpContentService, opt);
  }
  public async getSandbox(opt?: GetOption) {
    return this.get<Sandbox>(Sandbox, opt);
  }
  public async getGotty(opt?: GetOption) {
    let tty: GottyClient | null = null;
    try {
      tty = await this.get<GottyClient>(GottyClient, opt);
    } catch (error) {
      if (!tty) {
        this.bind(GottyClient, this.bindGotty);
        tty = await this.get<GottyClient>(GottyClient, opt);
      }
    }
    return tty;
  }
  public async getSandboxConfig(opt?: GetOption) {
    return this.get<SandboxConfig>(SandboxConfigKey, opt);
  }
  public async checkOrApplySandbox() {
    try {
      const needCheck = this.hasModuleExported(Sandbox);
      const sandbox = await this.getSandbox();
      if (needCheck) {
        await sandbox.check();
      }
      return sandbox;
    } catch (error) {
      if (error instanceof HttpClientRequestError) throw error;
      this.clearSandbox();
      return this.getSandbox();
    }
  }
  protected async bindContentService() {
    const { token, isProd } = await this.getSandboxConfig();
    const service = new HttpContentService({ token, isProd });
    service.onUnavailable(() => {
      this.clearSandboxConfig();
    });
    return service;
  }
  protected async bindSandbox() {
    try {
      const { token, poolName } = await this.getSandboxConfig();
      const info = await applySandboxByPoolName({ poolName, token });
      const sandbox = new Sandbox(info);
      sandbox.on(SandboxEvent.Broken, () => {
        this.clearSandbox();
      });
      return sandbox;
    } catch (error) {
      if (error instanceof UnavailableTokenError) {
        this.clearSandboxConfig();
      }
      throw error;
    }
  }
  protected async bindGotty() {
    const sandbox = await this.checkOrApplySandbox();
    const tty = new GottyClient(sandbox);
    const { reject, resolve, promise } = createPromise<GottyClient>();
    tty.on(TTYEvent.Ready, () => resolve(tty));
    tty.on(TTYEvent.Unavailable, (e) => {
      sandbox.kill().finally(() => {
        this.clearSandbox();
        reject(e);
      });
    });
    tty.onFail((e: any) => {
      this.clearGotty();
      reject(e);
    });
    return promise;
  }
}

type Cb<T> = () => T | PromiseLike<T>;

export function createSandboxContainer(cb: Cb<SandboxConfig>) {
  const container = new SandboxContainer();
  container.bind(SandboxConfigKey, cb);
  return container;
}

export function createContainer(sandboxConfig: SandboxConfig) {
  /** 由于获取token接口中返回的code表示的业务状态需要前置处理，所以把获取config的接口提前 */
  return createSandboxContainer(() => sandboxConfig);
}
